import { useState } from 'react';
import { PaginationProps, UsePaginationResult } from './types';

/**
 * ページネーションのロジックを管理するカスタムフック
 */
export const usePagination = ({
  currentPage,
  totalPages,
  onPageChange,
}: PaginationProps): UsePaginationResult => {
  // expanded が true なら中央表示範囲を拡大する
  const [expanded, setExpanded] = useState(false);

  const getPageNumbers = (): Array<number | 'left-ellipsis' | 'right-ellipsis'> => {
    const pages: Array<number | 'left-ellipsis' | 'right-ellipsis'> = [];

    if (totalPages <= 7) {
      // 総ページ数が少ない場合はすべて表示
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // 先頭ページは常に表示
      pages.push(1);

      // 左側の省略記号を表示するかどうか
      if (currentPage > (expanded ? 3 : 4)) {
        pages.push('left-ellipsis');
      } else {
        // 省略せず、2～currentPage-1 を表示（ただし上限は 4 まで）
        for (let i = 2; i < Math.min(currentPage, 5); i++) {
          pages.push(i);
        }
      }

      // 中央部分の開始・終了を計算
      const computedStart = Math.max(currentPage - (expanded ? 2 : 1), 2);
      const computedEnd = Math.min(currentPage + (expanded ? 2 : 1), totalPages - 1);
      for (let i = computedStart; i <= computedEnd; i++) {
        // 重複を避けるために、既に入っていなければ追加
        if (!pages.includes(i)) {
          pages.push(i);
        }
      }

      // 右側の省略記号を表示するかどうか
      if (currentPage < totalPages - (expanded ? 2 : 3)) {
        pages.push('right-ellipsis');
      } else {
        for (let i = Math.max(totalPages - 3, 2); i < totalPages; i++) {
          if (!pages.includes(i)) {
            pages.push(i);
          }
        }
      }

      // 最終ページ
      pages.push(totalPages);
    }

    return pages;
  };

  const pages = getPageNumbers();

  // 中央部分の最初と最後のページ番号を計算
  const computedStart = Math.max(currentPage - (expanded ? 2 : 1), 2);
  const computedEnd = Math.min(currentPage + (expanded ? 2 : 1), totalPages - 1);

  // 省略記号クリック時のハンドラ
  const handleEllipsisClick = (position: 'left' | 'right'): void => {
    if (position === 'left') {
      onPageChange(computedStart);
    } else if (position === 'right') {
      onPageChange(computedEnd + 1);
    }
    
    // 必要に応じて expanded 状態も変更する
    if (!expanded) {
      setExpanded(true);
    }
  };

  return {
    pages,
    expanded,
    handleEllipsisClick,
    setExpanded,
  };
}; 